/**
 * ChatResponse Component
 * Displays AI chat responses in conversational format with Markdown support
 * and structured content rendering (JSON, Cards, Sections, Tips)
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { parseResponse, generateAutoTrustScore, extractAutoTags } from '../utils/responseParser';
import { StructuredResponse } from './structured';
import { SearchingAnimation } from './loading';
import { ResultsLayout, ResultCard, TrustScoreBadge } from './results';
import { AnimatedContainer, StaggeredList } from './animations';
import './ChatResponse.css';
import '../styles/structured.css';

interface ChatResponseProps {
  content: string;
  isLoading?: boolean;
}

export const ChatResponse: React.FC<ChatResponseProps> = ({ content, isLoading = false }) => {
  // Show loading animation when loading
  if (isLoading) {
    return <SearchingAnimation />;
  }

  // Don't show anything if content is empty or the default message
  if (!content || content.trim() === '' || 
      content.includes('No hay resultados todavía') ||
      content.includes('Escribe una consulta')) {
    return null;
  }

  // Parse the response to detect its type
  const parsed = parseResponse(content);

  // Get layout configuration from window.ai360Config if available
  const config = (window as any).ai360Config || {};
  const showSections = config.showSections !== false;
  const resultsLayout = config.resultsLayout || 'list';
  const gridColumns = config.gridColumns || 2;
  const titleMaxLength = config.titleMaxLength || 60;
  const descriptionMaxLength = config.descriptionMaxLength || 150;
  const entryAnimations = config.entryAnimations !== false;
  const hoverEffects = config.hoverEffects !== false;

  // If structured JSON response with sections/cards/tips, use ResultsLayout
  if (parsed.type === 'json' && 
      (parsed.content.sections || parsed.content.cards || parsed.content.tips)) {
    // Map the parsed content to ResultsLayout format
    const sections = parsed.content.sections?.map((section: any, sectionIndex: number) => {
      // Check if section has cards already (from URL extraction)
      if (section.cards && section.cards.length > 0) {
        return {
          title: section.title,
          icon: section.icon,
          items: section.cards.map((card: any, cardIndex: number) => ({
            title: card.title,
            description: card.description || '',
            icon: card.icon,
            trustScore: card.trustScore || generateAutoTrustScore(cardIndex),
            tags: card.tags || extractAutoTags(card.title + ' ' + (card.description || '')),
            url: card.url || card.link,
          })),
        };
      }
      
      // Check if section has items array
      if (section.items && section.items.length > 0) {
        return {
          title: section.title,
          icon: section.icon,
          items: section.items.map((item: any, itemIndex: number) => ({
            title: item.title,
            description: item.description || '',
            icon: item.icon,
            trustScore: item.trustScore || generateAutoTrustScore(itemIndex),
            tags: item.tags || extractAutoTags(item.title + ' ' + (item.description || '')),
            url: item.url || item.link,
          })),
        };
      }
      
      // If section has substantial content (not empty, not "---"), create ONE card
      const trimmedContent = section.content?.trim() || '';
      if (trimmedContent && trimmedContent !== '---' && trimmedContent !== '-' && trimmedContent.length > 10) {
        // Extract domain from URL as fallback title
        const getTitleFromUrl = (url?: string): string => {
          if (!url) return section.title;
          try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace('www.', '');
          } catch {
            return section.title;
          }
        };

        return {
          title: section.title,
          icon: section.icon,
          items: [{
            title: section.url ? getTitleFromUrl(section.url) : section.title,
            description: trimmedContent,
            icon: section.icon,
            trustScore: generateAutoTrustScore(0),
            tags: extractAutoTags(section.title + ' ' + trimmedContent),
            url: section.url,
          }],
        };
      }
      
      // Skip sections without valid content
      return null;
    }).filter(Boolean) || [];

    // Add cards as a separate section if present and no sections
    if (parsed.content.cards && !parsed.content.sections) {
      sections.push({
        title: parsed.content.title || 'Resultados',
        items: parsed.content.cards.map((card: any, cardIndex: number) => ({
          title: card.title,
          description: card.description || card.content || '',
          icon: card.icon,
          trustScore: card.trustScore || generateAutoTrustScore(cardIndex),
          tags: card.tags || extractAutoTags(card.title + ' ' + (card.description || card.content || '')),
          url: card.link || card.url,
        })),
      });
    }

    // Map tips to ResultsLayout format
    const tips = parsed.content.tips?.map((tip: any) => ({
      text: tip.message || tip.text,
      type: tip.variant || tip.type || 'info',
    })) || [];

    // Get layout configuration from window.ai360Config if available
    const layoutConfig = window.ai360Config?.resultsLayout || {};
    const layout = resultsLayout === 'grid' ? 'grid' : 'list';

    return (
      <AnimatedContainer animation="fadeSlideUp">
        <ResultsLayout
          summary={parsed.content.summary}
          sections={sections}
          tips={tips}
          layout={layout}
          gridColumns={gridColumns}
          showSections={showSections}
          titleMaxLength={titleMaxLength}
          descriptionMaxLength={descriptionMaxLength}
          entryAnimations={entryAnimations}
          hoverEffects={hoverEffects}
        />
      </AnimatedContainer>
    );
  }

  // Fall back to original rendering for other types
  return (
    <div className="ai360-chat-response">
      <div className="ai360-chat-avatar">
        <svg 
          width="32" 
          height="32" 
          viewBox="0 0 32 32" 
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="16" cy="16" r="16" fill="currentColor" opacity="0.1"/>
          <path 
            d="M16 8C11.6 8 8 11.6 8 16C8 17.4 8.4 18.7 9.1 19.8L8 24L12.2 22.9C13.3 23.6 14.6 24 16 24C20.4 24 24 20.4 24 16C24 11.6 20.4 8 16 8ZM16 22C14.8 22 13.7 21.7 12.7 21.1L10.5 21.8L11.2 19.6C10.5 18.6 10 17.4 10 16C10 12.7 12.7 10 16 10C19.3 10 22 12.7 22 16C22 19.3 19.3 22 16 22Z" 
            fill="currentColor"
          />
          <circle cx="13" cy="16" r="1" fill="currentColor"/>
          <circle cx="16" cy="16" r="1" fill="currentColor"/>
          <circle cx="19" cy="16" r="1" fill="currentColor"/>
        </svg>
      </div>
      <div className="ai360-chat-content">
        {parsed.type === 'json' ? (
          <StructuredResponse data={parsed.content} />
        ) : parsed.type === 'markdown' ? (
          <ReactMarkdown>{parsed.content}</ReactMarkdown>
        ) : (
          <ReactMarkdown>{parsed.content}</ReactMarkdown>
        )}
      </div>
    </div>
  );
};
